import { httpRequest } from '@/services/httpClient'

export type TenantStatus = 'active' | 'inactive' | 'suspended'

export type Tenant = {
  tenantId: number
  name: string
  description?: string | null
  status: TenantStatus

  // Legal/Company
  legalName?: string | null
  taxNumber?: string | null
  taxOffice?: string | null
  industry?: string | null
  employeeRange?: string | null
  organizationType?: string | null
  foundationDate?: string | null

  // Address
  address?: string | null
  country?: string | null
  city?: string | null

  // Contact
  phone?: string | null
  email?: string | null
  website?: string | null
  authorizedPersonName?: string | null

  createdAt?: string
  lastUpdated?: string
}

export type TenantsListResponse = {
  items: Tenant[]
  totalCount: number
  page: number
  pageSize: number
}

export type TenantsListParams = {
  search?: string
  status?: TenantStatus | null
  page?: number
  pageSize?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export type CreateTenantPayload = Omit<Tenant, 'tenantId' | 'createdAt' | 'lastUpdated'>
export type UpdateTenantPayload = { id: number } & Partial<CreateTenantPayload>

export type CreateTenantWithAdminPayload = {
  tenant: {
    name: string
    status: TenantStatus
    legalName?: string | null
    taxNumber?: string | null
    taxOffice?: string | null
    industry?: string | null
    employeeRange?: string | null
    organizationType?: string | null
    foundationDate?: string | null
    description?: string | null
    address?: string | null
    country?: string | null
    city?: string | null
    phone?: string | null
    email?: string | null
    website?: string | null
    authorizedPersonName?: string | null
  }
  settings?: {
    themeColor?: string | null
    logoUrl?: string | null
    language?: string | null
    timezone?: string | null
  }
  adminUser: {
    userName: string
    email: string
    password: string
    phone?: string | null
    forcePasswordChange?: boolean
  }
}

export const tenantsApi = {
  list: (params: TenantsListParams) =>
    httpRequest<TenantsListResponse>('/v1/tenants', {
      method: 'GET',
      query: {
        search: params.search ?? undefined,
        status: params.status ?? undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 10,
        sortBy: params.sortBy ?? undefined,
        sortDir: params.sortDir ?? undefined,
      },
    }),

  getById: (id: number) => httpRequest<Tenant>(`/v1/tenants/${id}`, { method: 'GET' }),

  createWithAdmin: (payload: CreateTenantWithAdminPayload) =>
    httpRequest<{ success: boolean; data?: { tenantId?: number; adminUserId?: number }; message?: string }>(
      '/v1/tenants/create-with-admin',
      {
        method: 'POST',
        body: payload,
      },
    ),

  create: (payload: CreateTenantPayload) =>
    httpRequest<{ success: boolean; data?: { id?: number }; message?: string }>('/v1/tenants', {
      method: 'POST',
      body: payload,
    }),

  update: (payload: UpdateTenantPayload) =>
    httpRequest<{ success: boolean; data?: { id?: number }; message?: string }>(`/v1/tenants/${payload.id}`, {
      method: 'PUT',
      body: payload,
    }),

  updateSettings: (tenantId: number, payload: { themeColor?: string | null; logoUrl?: string | null; language?: string | null; timezone?: string | null }) =>
    httpRequest<{ success: boolean; data?: { tenantId?: number }; message?: string }>(`/v1/tenants/${tenantId}/settings`, {
      method: 'PUT',
      body: { tenantId, ...payload },
    }),

  delete: (id: number) =>
    httpRequest<{ success: boolean; message?: string }>(`/v1/tenants/${id}`, { method: 'DELETE' }),
}

